import { useQuery } from '@tanstack/react-query';

import { walletApi } from '@/services/api/wallet.api';
import { useAuth } from './useAuth';

import {
  NATIVE_USDC_CONTRACT_ADDRESS,
  PUSD_CONTRACT_ADDRESS,
  USDC_E_CONTRACT_ADDRESS,
} from '@/constants/tokens.constants';

export interface WalletTransaction {
  hash: string;
  blockNum: string;
  from: string;
  to: string;
  value: number;
  asset: string;
  category: string;
  rawContract: {
    value: string;
    address: string;
    decimal: string;
  };
  metadata: {
    blockTimestamp: string;
  };
  type: 'Deposit' | 'Withdrawal';
  timestamp: Date;
}

const INTERNAL_ADDRESSES = [
  '0xE111180000d2663C0091e4f400237545B87B996B', // CTF Exchange
  '0xe2222d279d744050d28e00520010520000310F59', // Neg Risk CTF Exchange
  '0x4D97DCd97eC945f40cF65F87097ACe5EA0476045', // CTF
  '0xd91E80cF2E7be2e162c6513ceD06f1dD0dA35296', // Neg Risk Adapter
  '0xAdA100Db00Ca00073811820692005400218FcE1f', // CTF Collateral Adapter
  '0xadA2005600Dec949baf300f4C6120000bDB6eAab', // Neg Risk Collateral Adapter
  '0x93070a847efEf7F70739046A929D47a521F5B8ee', // Collateral Onramp
  '0x2957922eb93258b93368531d39facca3b4dc5854', // Collateral Offramp
  '0x814C849e0B59a2dfE5d8B72fF7A2dbC849844053', // Handle Ops Relayer
  '0x26b0382ff2f95400f9bb49f87c2fb24054291868', // Known Transfer Relayer
  '0x295bf293798cf0cc27d4da047910ff61a053dd12', // Proxy Op Contract
  // '0x08fff5b9a79576a7c6e18a9d05ece0658a34ba79', // Trade Token Transfer
].map((a) => a.toLowerCase());

export const useWalletHistory = () => {
  const { user, isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['wallet-history-all', user?.walletAddress, user?.proxyWalletAddress],
    queryFn: async (): Promise<WalletTransaction[]> => {
      const apiKey = process.env.NEXT_PUBLIC_ALCHEMY_BEARER_TOKEN || 'T7bAMb2Gq3a6hknoxsEpo';
      const addressesToWatch: string[] = [];

      if (user?.walletAddress) addressesToWatch.push(user.walletAddress.toLowerCase());
      if (user?.proxyWalletAddress) addressesToWatch.push(user.proxyWalletAddress.toLowerCase());

      if (addressesToWatch.length === 0) return [];

      const contractAddresses = [
        NATIVE_USDC_CONTRACT_ADDRESS,
        USDC_E_CONTRACT_ADDRESS,
        PUSD_CONTRACT_ADDRESS,
      ].filter(Boolean);

      const primaryAddress = addressesToWatch[addressesToWatch.length - 1]; // prefer proxy

      const baseParams = {
        fromBlock: '0x0',
        toBlock: 'latest',
        category: ['external', 'erc20'],
        contractAddresses: contractAddresses.length > 0 ? contractAddresses : undefined,
        withMetadata: true,
        excludeZeroValue: true,
        maxCount: '0x3E8', // Alchemy max: 1000 per page request
        order: 'desc',
      };

      // Fetch all deposits and withdrawals in parallel
      const [rawDeposits, rawWithdrawals] = await Promise.all([
        walletApi.getAssetTransfers(apiKey, { ...baseParams, toAddress: primaryAddress }),
        walletApi.getAssetTransfers(apiKey, { ...baseParams, fromAddress: primaryAddress }),
      ]);

      const allTransfers: WalletTransaction[] = [
        ...rawDeposits.map((tx) => ({
          ...tx,
          type: 'Deposit' as const,
          timestamp: new Date(tx.metadata.blockTimestamp),
        })),
        ...rawWithdrawals.map((tx) => ({
          ...tx,
          type: 'Withdrawal' as const,
          timestamp: new Date(tx.metadata.blockTimestamp),
        })),
      ];

      // Filter out self-transfers first
      let filtered = allTransfers.filter((tx) => {
        const from = tx.from?.toLowerCase();
        const to = tx.to?.toLowerCase();
        if (addressesToWatch.includes(from) && addressesToWatch.includes(to)) return false;
        return true;
      });

      // Deduplicate by hash + type
      const unique = Array.from(
        new Map(filtered.map((tx) => [`${tx.hash}-${tx.type}`, tx])).values()
      );

      // Fetch top-level transaction data to find the actual "Interacted With (To)"
      const hashes = unique.map((tx) => tx.hash);
      const txDetailsMap = await walletApi.getTransactionsByHashes(apiKey, hashes);

      // Filter out internal contract interactions using top-level `to` address
      const finallyFiltered = unique.filter((tx) => {
        const topLevelTx = txDetailsMap[tx.hash];
        const topLevelTo = topLevelTx?.to?.toLowerCase();

        // If the top-level interaction was with an internal address, filter it out!
        if (topLevelTo && INTERNAL_ADDRESSES.includes(topLevelTo)) return false;

        // Fallback: if we somehow missed the top-level, check the token transfer `from` / `to`
        const from = tx.from?.toLowerCase();
        const to = tx.to?.toLowerCase();
        if (INTERNAL_ADDRESSES.includes(from) || INTERNAL_ADDRESSES.includes(to)) return false;

        return true;
      });

      // Sort newest first
      finallyFiltered.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

      return finallyFiltered;
    },
    enabled: isAuthenticated && !!(user?.walletAddress || user?.proxyWalletAddress),
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes — no repeat API calls on tab switch
  });
};
